import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import {
  useRequestQrMutation,
  useSendCodeMutation,
  useSendPasswordMutation,
  useSendPhoneMutation,
  useGetQrQuery,
} from '@app/api/IntegrationAuth';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';

import {
  QR_POLLING_INTERVAL_MS,
  QR_WAIT_LIMIT_MS,
  RESEND_COOLDOWN_SECONDS,
  SCANNED_WAIT_LIMIT_MS,
} from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/constants';
import {
  AuthStepState,
  type AuthStepInfo,
} from '@pages/Integrations/IntegrationsList/components/ConnectIntegrationModal/types';

export type QrSessionMode = 'qr' | 'phone';

// Ретраи requestQR: первый запрос может упасть (например, шлюз ещё не знает
// свежий токен интеграции — task/10891), и без повтора окно навсегда
// оставалось на скелетоне (task/10896). Прежние восемь попыток по 15 секунд
// прятали причину на две минуты: человек всё это время видел скелетон.
// Теперь короткая серия, после неё — честная ошибка и кнопка повтора, за
// которой снова стоит такая же ограниченная серия (issue/11035).
const REQUEST_QR_RETRY_DELAY_MS = 5_000;
const REQUEST_QR_MAX_ATTEMPTS = 3;

let qrAuthAttemptSequence = 0;

const nextQrAuthAttemptId = () => {
  qrAuthAttemptSequence += 1;

  return qrAuthAttemptSequence;
};

interface UseQrSessionAuthParams {
  integrationId: number | null;
  activeBranchId: number | null;
  stepInfo: AuthStepInfo;
  isActive: boolean;
}

/**
 * Логика архетипа «QR-сессия» (task/10730): QR — основной вход, запасной —
 * номер и код. QR обновляется перезапросом GetQr (шлюз на каждый вызов
 * выпускает актуальный код), успех приходит сам через useIntegrationAuthState.
 */
export const useQrSessionAuth = ({
  integrationId,
  activeBranchId,
  stepInfo,
  isActive,
}: UseQrSessionAuthParams) => {
  const { showNotification } = useContext(NotificationContext);

  const [mode, setMode] = useState<QrSessionMode>('qr');
  // Поллинг QR стартует только после ответа requestQR: первый GetQr,
  // ушедший раньше выпуска кода, получал 500 «QR code not requested yet»
  // на каждое открытие окна (task/10887).
  const [isQrRequested, setIsQrRequested] = useState(false);
  const [qrAuthAttemptId, setQrAuthAttemptId] = useState<number | null>(null);
  const [isReplacingScannedQr, setIsReplacingScannedQr] = useState(false);
  const [getQrError, setGetQrError] = useState<string | null>(null);
  const [isCodeStage, setIsCodeStage] = useState(false);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);
  const [phone, setPhone] = useState('');
  const resendIntervalRef = useRef<number | null>(null);
  // При входе по номеру CRM может ещё хранить auth_failed прошлой QR-попытки.
  // Его нельзя показывать поверх только что открытой формы. Запоминаем шаг, на
  // котором реально отправили номер: Error становится новым либо после
  // перехода сервера через следующий шаг, либо сразу, если попытка начиналась
  // не из Error.
  const phoneAttemptBaselineStateRef = useRef<AuthStepState | null>(null);
  const phoneProgressObservedRef = useRef(false);
  const [isPhoneAuthError, setIsPhoneAuthError] = useState(false);

  const [requestQr] = useRequestQrMutation();
  const [sendPhone, { isLoading: isPhoneSending }] = useSendPhoneMutation();
  const [sendCode, { isLoading: isCodeSending }] = useSendCodeMutation();
  const [sendPassword, { isLoading: isPasswordSending }] =
    useSendPasswordMutation();

  const isQrVisible =
    isActive &&
    mode === 'qr' &&
    isQrRequested &&
    ([
      AuthStepState.Loading,
      AuthStepState.QrReady,
      AuthStepState.QrExpired,
      // Сохранённый auth_failed/error может быть состоянием прошлой попытки.
      // После успешного RequestQr свежий GetQr должен суметь вывести новый QR.
      AuthStepState.Error,
    ].includes(stepInfo.state) ||
      // После явного повтора из qr_scanned локальная попытка уже новая, а
      // сохранённый серверный шаг может обновиться чуть позже HTTP-ответа.
      // Для обычного скана эта ветка выключена: старый QR не должен заслонять
      // экран подтверждения и его минутный таймаут.
      (stepInfo.state === AuthStepState.QrScanned && isReplacingScannedQr));

  const {
    currentData: qrData,
    isError: isGetQrError,
    error: getQrQueryError,
  } = useGetQrQuery(
    isQrVisible && integrationId && activeBranchId && qrAuthAttemptId
      ? {
          ActiveBranchId: activeBranchId,
          integrationId,
          authAttemptId: qrAuthAttemptId,
        }
      : skipToken,
    {
      pollingInterval: QR_POLLING_INTERVAL_MS,
      refetchOnMountOrArgChange: true,
    }
  );

  // Каждая серия RequestQr владеет своей generation, in-flight запросом и
  // таймером. Ответ старой интеграции/попытки не может менять новую сессию.
  const requestedForIdentityRef = useRef<string | null>(null);
  const forceQrRequestRef = useRef(false);
  const requestQrGenerationRef = useRef(0);
  const requestQrPromiseRef = useRef<ReturnType<typeof requestQr> | null>(null);
  const requestQrInFlightRef = useRef(false);
  const retryTimerRef = useRef<number | null>(null);
  const activeSeriesRef = useRef<{
    generation: number;
    startedState: AuthStepState;
    reason: 'initial' | 'retry' | 'scanned';
  } | null>(null);
  const [isRequestQrExhausted, setIsRequestQrExhausted] = useState(false);
  const [requestQrError, setRequestQrError] = useState<string | null>(null);
  const [isRequestQrInFlight, setIsRequestQrInFlight] = useState(false);

  const authContextRef = useRef({
    integrationId,
    activeBranchId,
    isActive,
    mode,
    stepState: stepInfo.state,
  });
  authContextRef.current = {
    integrationId,
    activeBranchId,
    isActive,
    mode,
    stepState: stepInfo.state,
  };

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const cancelRequestQrSeries = useCallback(
    (updateState = true) => {
      requestQrGenerationRef.current += 1;
      clearRetryTimer();
      requestQrPromiseRef.current?.abort?.();
      requestQrPromiseRef.current = null;
      requestQrInFlightRef.current = false;
      activeSeriesRef.current = null;

      if (updateState) {
        setIsRequestQrInFlight(false);
      }
    },
    [clearRetryTimer]
  );

  const activateQrPolling = useCallback(() => {
    setRequestQrError(null);
    setGetQrError(null);
    setIsRequestQrExhausted(false);
    setQrAuthAttemptId(nextQrAuthAttemptId());
    setIsQrRequested(true);
  }, []);

  const startRequestQrSeries = useCallback(
    (reason: 'initial' | 'retry' | 'scanned') => {
      const { current } = authContextRef;
      if (
        requestQrInFlightRef.current ||
        !current.isActive ||
        current.mode !== 'qr' ||
        !current.integrationId ||
        !current.activeBranchId
      ) {
        return false;
      }

      clearRetryTimer();
      const generation = requestQrGenerationRef.current + 1;
      requestQrGenerationRef.current = generation;
      requestQrInFlightRef.current = true;
      activeSeriesRef.current = {
        generation,
        startedState: current.stepState,
        reason,
      };

      setIsRequestQrInFlight(true);
      setIsQrRequested(false);
      setQrAuthAttemptId(null);
      setGetQrError(null);
      setIsRequestQrExhausted(false);
      setRequestQrError(null);

      let failedAttempts = 0;
      const expectedIntegrationId = current.integrationId;
      const expectedBranchId = current.activeBranchId;

      const isCurrentSeries = () => {
        const latest = authContextRef.current;

        return (
          requestQrGenerationRef.current === generation &&
          latest.isActive &&
          latest.mode === 'qr' &&
          latest.integrationId === expectedIntegrationId &&
          latest.activeBranchId === expectedBranchId
        );
      };

      const finishSeries = () => {
        if (!isCurrentSeries()) {
          return;
        }

        requestQrPromiseRef.current = null;
        requestQrInFlightRef.current = false;
        activeSeriesRef.current = null;
        setIsRequestQrInFlight(false);
      };

      const canRetryInCurrentStep = () => {
        const latestStep = authContextRef.current.stepState;
        if (reason === 'scanned') {
          return [
            AuthStepState.QrScanned,
            AuthStepState.Loading,
            AuthStepState.Error,
          ].includes(latestStep);
        }

        return (
          latestStep === AuthStepState.Loading ||
          latestStep === AuthStepState.Error ||
          latestStep === current.stepState
        );
      };

      const runAttempt = async () => {
        if (!isCurrentSeries()) {
          return;
        }

        const requestPromise = requestQr({
          ActiveBranchId: expectedBranchId,
          integrationId: expectedIntegrationId,
          // Только первая transport-попытка явного повтора инвалидирует
          // старый код. Если ответ потерялся, последующие попытки забирают
          // уже выпущенный QR и не создают churn каждые пять секунд.
          ...(reason !== 'initial' && failedAttempts === 0
            ? { force: true }
            : {}),
        });
        requestQrPromiseRef.current = requestPromise;

        try {
          await requestPromise.unwrap();
          if (!isCurrentSeries()) {
            return;
          }

          setRequestQrError(null);
          // Уникальная cache key не даёт RTK Query вернуть QR прошлой попытки
          // того же аккаунта; currentData ниже не переносит QR другого arg.
          setQrAuthAttemptId(nextQrAuthAttemptId());
          setIsQrRequested(true);
          finishSeries();
        } catch (error) {
          if (!isCurrentSeries()) {
            return;
          }

          failedAttempts += 1;
          const errorMessage = handleCatchError(error);
          setRequestQrError(errorMessage);

          if (failedAttempts >= REQUEST_QR_MAX_ATTEMPTS) {
            setIsQrRequested(false);
            setIsRequestQrExhausted(true);
            finishSeries();
            return;
          }

          if (!canRetryInCurrentStep()) {
            finishSeries();
            return;
          }

          retryTimerRef.current = window.setTimeout(() => {
            retryTimerRef.current = null;
            void runAttempt();
          }, REQUEST_QR_RETRY_DELAY_MS);
        }
      };

      void runAttempt();

      return true;
    },
    [clearRetryTimer, requestQr]
  );

  useEffect(() => {
    if (!isActive || !integrationId || !activeBranchId || mode !== 'qr') {
      requestedForIdentityRef.current = null;
      cancelRequestQrSeries();
      return;
    }

    const identity = `${activeBranchId}:${integrationId}`;
    if (requestedForIdentityRef.current === identity) {
      return;
    }

    requestedForIdentityRef.current = identity;
    cancelRequestQrSeries();
    const shouldForceRequest = forceQrRequestRef.current;
    forceQrRequestRef.current = false;

    // Свежий qr_ready уже доказывает, что шлюз выпустил код. Дополнительный
    // RequestQr только создаёт окно гонки: состояние успевало стать QrReady,
    // эффект ниже отменял ещё не завершившийся запрос, а GetQr так и не
    // включался. Сразу читаем готовый код.
    if (stepInfo.state === AuthStepState.QrReady && !shouldForceRequest) {
      activateQrPolling();

      return;
    }

    const isWaitingForUser = [
      AuthStepState.QrScanned,
      AuthStepState.CodeSent,
      AuthStepState.Password2fa,
      AuthStepState.Success,
    ].includes(stepInfo.state);

    if (!isWaitingForUser || shouldForceRequest) {
      startRequestQrSeries(shouldForceRequest ? 'retry' : 'initial');
    }
  }, [
    activeBranchId,
    activateQrPolling,
    cancelRequestQrSeries,
    integrationId,
    isActive,
    mode,
    startRequestQrSeries,
    stepInfo.state,
  ]);

  useEffect(
    () => () => {
      cancelRequestQrSeries(false);
    },
    [cancelRequestQrSeries]
  );

  useEffect(() => {
    setIsReplacingScannedQr(false);
    phoneAttemptBaselineStateRef.current = null;
    phoneProgressObservedRef.current = false;
    setIsPhoneAuthError(false);
  }, [activeBranchId, integrationId]);

  useEffect(() => {
    if (stepInfo.state !== AuthStepState.QrScanned) {
      setIsReplacingScannedQr(false);
    }
  }, [stepInfo.state]);

  const retryRequestQr = useCallback(() => {
    startRequestQrSeries('retry');
  }, [startRequestQrSeries]);

  // Если состояние ушло дальше того, на котором стартовала серия, старый
  // HTTP-ответ и его будущий retry уже не имеют права менять новый шаг.
  useEffect(() => {
    const activeSeries = activeSeriesRef.current;
    if (!activeSeries || stepInfo.state === activeSeries.startedState) {
      return;
    }

    // Сервер успел подтвердить выпуск QR раньше HTTP-ответа RequestQr.
    // Состояние сервера авторитетнее незавершённого transport-запроса:
    // снимаем его и немедленно запускаем GetQr вместо вечного skeleton.
    if (stepInfo.state === AuthStepState.QrReady) {
      cancelRequestQrSeries();
      activateQrPolling();

      return;
    }

    const isPreparatoryTransition =
      [AuthStepState.Loading, AuthStepState.Error].includes(
        activeSeries.startedState
      ) &&
      [AuthStepState.Loading, AuthStepState.Error].includes(stepInfo.state);
    const isScannedRetryTransition =
      activeSeries.reason === 'scanned' &&
      activeSeries.startedState === AuthStepState.QrScanned &&
      [AuthStepState.Loading, AuthStepState.Error].includes(stepInfo.state);

    if (!isPreparatoryTransition && !isScannedRetryTransition) {
      cancelRequestQrSeries();
    }
  }, [activateQrPolling, cancelRequestQrSeries, stepInfo.state]);

  // Один неуспешный GetQr уже означает, что текущая попытка не может отдать
  // код. Останавливаем опасный для Telegram бесконечный поллинг и даём новый
  // ограниченный RequestQr по кнопке.
  useEffect(() => {
    if (!isQrVisible || !isGetQrError) {
      return;
    }

    setGetQrError(
      handleCatchError(getQrQueryError) ||
        'Не удалось получить QR-код. Попробуйте ещё раз.'
    );
    setIsQrRequested(false);
    setQrAuthAttemptId(null);
  }, [getQrQueryError, isGetQrError, isQrVisible]);

  // Успешный HTTP без QR тоже не должен оставлять вечный скелетон. Даём
  // поллингу две полные попытки и затем предлагаем начать авторизацию заново.
  useEffect(() => {
    if (
      !isQrVisible ||
      !isQrRequested ||
      isGetQrError ||
      qrData?.Qr ||
      getQrError
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      setGetQrError('Шлюз не выдал QR-код вовремя. Попробуйте ещё раз.');
      setIsQrRequested(false);
      setQrAuthAttemptId(null);
    }, QR_WAIT_LIMIT_MS);

    return () => window.clearTimeout(timer);
  }, [getQrError, isGetQrError, isQrRequested, isQrVisible, qrData?.Qr]);

  const startResendCooldown = useCallback(() => {
    setResendSecondsLeft(RESEND_COOLDOWN_SECONDS);
    if (resendIntervalRef.current) {
      window.clearInterval(resendIntervalRef.current);
    }
    resendIntervalRef.current = window.setInterval(() => {
      setResendSecondsLeft((seconds) => {
        if (seconds <= 1 && resendIntervalRef.current) {
          window.clearInterval(resendIntervalRef.current);
          resendIntervalRef.current = null;
        }
        return Math.max(0, seconds - 1);
      });
    }, 1000);
  }, []);

  useEffect(
    () => () => {
      if (resendIntervalRef.current) {
        window.clearInterval(resendIntervalRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (mode !== 'phone' || phoneAttemptBaselineStateRef.current === null) {
      setIsPhoneAuthError(false);

      return;
    }

    if (stepInfo.state !== AuthStepState.Error) {
      if (phoneAttemptBaselineStateRef.current === AuthStepState.Error) {
        phoneProgressObservedRef.current = true;
      }
      setIsPhoneAuthError(false);

      return;
    }

    setIsPhoneAuthError(
      phoneProgressObservedRef.current ||
        phoneAttemptBaselineStateRef.current !== AuthStepState.Error
    );
  }, [mode, stepInfo.state]);

  const submitPhone = useCallback(
    async (phoneValue: string) => {
      if (!integrationId || !activeBranchId || !phoneValue) {
        return;
      }
      try {
        await sendPhone({
          ActiveBranchId: activeBranchId,
          integrationId,
          data: { Value: phoneValue },
        }).unwrap();
        phoneAttemptBaselineStateRef.current = authContextRef.current.stepState;
        phoneProgressObservedRef.current = false;
        setIsPhoneAuthError(false);
        setPhone(phoneValue);
        setIsCodeStage(true);
        startResendCooldown();
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [
      integrationId,
      activeBranchId,
      sendPhone,
      startResendCooldown,
      showNotification,
    ]
  );

  const submitCode = useCallback(
    async (code: string) => {
      if (!integrationId || !activeBranchId || !code) {
        return;
      }
      try {
        await sendCode({
          ActiveBranchId: activeBranchId,
          integrationId,
          data: { Value: code },
        }).unwrap();
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [integrationId, activeBranchId, sendCode, showNotification]
  );

  const submitPassword = useCallback(
    async (password: string) => {
      if (!integrationId || !activeBranchId || !password) {
        return;
      }
      try {
        await sendPassword({
          ActiveBranchId: activeBranchId,
          integrationId,
          data: { Value: password },
        }).unwrap();
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [integrationId, activeBranchId, sendPassword, showNotification]
  );

  /**
   * Ожидание после скана не бесконечно. Шаг «Код отсканирован» был
   * единственным состоянием окна без предела и без действий: если шлюз не
   * присылал следующий шаг, человеку оставалось только закрыть окно
   * (issue/11036).
   */
  const [isScannedWaitOver, setIsScannedWaitOver] = useState(false);
  const [scannedWaitAttempt, setScannedWaitAttempt] = useState(0);

  useEffect(() => {
    if (mode !== 'qr' || stepInfo.state !== AuthStepState.QrScanned) {
      setIsScannedWaitOver(false);

      return;
    }

    const timer = window.setTimeout(
      () => setIsScannedWaitOver(true),
      SCANNED_WAIT_LIMIT_MS
    );

    return () => window.clearTimeout(timer);
  }, [integrationId, mode, scannedWaitAttempt, stepInfo.state]);

  const retryScanned = useCallback(() => {
    if (!startRequestQrSeries('scanned')) {
      return;
    }

    setIsReplacingScannedQr(true);
    setIsScannedWaitOver(false);
    setScannedWaitAttempt((attempt) => attempt + 1);
  }, [startRequestQrSeries]);

  const switchToPhone = useCallback(() => {
    // Вход по номеру — отдельный путь: держать в фоне повтор запроса кода
    // незачем.
    cancelRequestQrSeries();
    setIsQrRequested(false);
    setQrAuthAttemptId(null);
    setGetQrError(null);
    setIsRequestQrExhausted(false);
    setRequestQrError(null);
    setIsScannedWaitOver(false);
    setIsReplacingScannedQr(false);
    phoneAttemptBaselineStateRef.current = null;
    phoneProgressObservedRef.current = false;
    setIsPhoneAuthError(false);
    setMode('phone');
  }, [cancelRequestQrSeries]);
  const switchToQr = useCallback(() => {
    cancelRequestQrSeries();
    requestedForIdentityRef.current = null;
    forceQrRequestRef.current = true;
    setIsQrRequested(false);
    setQrAuthAttemptId(null);
    setGetQrError(null);
    setIsRequestQrExhausted(false);
    setRequestQrError(null);
    setIsReplacingScannedQr(false);
    phoneAttemptBaselineStateRef.current = null;
    phoneProgressObservedRef.current = false;
    setIsPhoneAuthError(false);
    setMode('qr');
    setIsCodeStage(false);
  }, [cancelRequestQrSeries]);

  const retryPhoneAuth = useCallback(() => {
    phoneAttemptBaselineStateRef.current = null;
    phoneProgressObservedRef.current = false;
    setIsPhoneAuthError(false);
    setIsCodeStage(false);
    setPhone('');
    setResendSecondsLeft(0);
    if (resendIntervalRef.current) {
      window.clearInterval(resendIntervalRef.current);
      resendIntervalRef.current = null;
    }
  }, []);

  return {
    mode,
    qr: isQrVisible ? qrData?.Qr : undefined,
    getQrError,
    isQrLoading:
      isRequestQrInFlight || (isQrVisible && isQrRequested && !qrData?.Qr),
    isRequestQrInFlight,
    isRequestQrExhausted,
    requestQrError,
    retryRequestQr,
    retryScanned,
    isScannedWaitOver,
    phone,
    isCodeStage,
    resendSecondsLeft,
    isPhoneSending,
    isCodeSending,
    isPasswordSending,
    isPhoneAuthError,
    submitPhone,
    submitCode,
    submitPassword,
    switchToPhone,
    switchToQr,
    retryPhoneAuth,
  };
};
